/* ═══════════════════════════════════════════════════════════════
   REVIEW — Revert as a review (Tweaks 36).

   History → Revert no longer writes the published master. It opens the
   same Review the Sync road uses: OURS = the draft on screen, THEIRS = the
   older version's text, run through the one engine (merge3 → planReview).
   Every paragraph that would change back is one pending change:
     accept = take the old words back · reject = keep today's.
   Undecided reads as reject (keep today's). The result is written into the
   DRAFT by the host; Publish stays the only road out (SPEC_WORKFLOW).
   Pure — no editor, no I/O.
   ═══════════════════════════════════════════════════════════════ */

import { merge3, planReview, type Change, type Decision } from '../../shared/sync';
import type { ContentBlock } from '../../shared/types';

/** The changes that would take `ours` back to `theirs`. With ours as the
 *  base, every difference is an "upstream" change — Accept applies it. */
export function revertChanges(ours: ContentBlock[], theirs: ContentBlock[]): Change[] {
  return merge3(ours, ours, theirs).filter((c) => c.kind !== 'reconciled');
}

export interface RevertResult {
  blocks: ContentBlock[];
  /** Engine count of applied changes. */
  applied: number;
  rejected: number;
  /** Changes the user accepted. */
  accepted: number;
  /** Every change was accepted — the draft now reads as the old version. */
  full: boolean;
}

/** Apply the accepted changes to `ours`; everything else keeps today's text. */
export function applyRevert(
  ours: ContentBlock[],
  changes: Change[],
  decisions: Record<string, Decision>
): RevertResult {
  const plan = planReview(ours, changes, decisions);
  const accepted = changes.filter((c) => decisions[c.id] === 'accept').length;
  return {
    blocks: plan.blocks,
    applied: plan.applied,
    rejected: plan.rejected,
    accepted,
    full: changes.length > 0 && accepted === changes.length,
  };
}

/** The History line: "Reverted to v1 (partial)" / "(full)". */
export function revertHistoryNote(target: string, full: boolean): string {
  return `Reverted to ${target} (${full ? 'full' : 'partial'})`;
}

/** Every change answered the same way (Accept all / Reject all). */
export function decideAll(changes: Change[], d: Decision): Record<string, Decision> {
  const out: Record<string, Decision> = {};
  for (const c of changes) out[c.id] = d;
  return out;
}
